import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import type { ContextMenuItem } from '@/components/ui/ContextMenu';
import { FunnelIcon, EyeSlashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PinLeftIcon, PinRightIcon } from '@/components/ui/icons/PinIcons';
import { useUIStore } from '@/stores/ui/ui-store';
import type { ColumnConfig, ColumnPinSide } from '@/stores/trades/mtm-column-store';

/**
 * Header-only context menu for the MTM table (LR-394). A trimmed sibling of
 * useTableContextMenus: MtmTable has no sorting and no cell selection, so this
 * exposes only Pin left / Pin right / Unpin / Hide column / Filter by <col>.
 * Pin/Hide actions also flash the column in the sidebar Columns tab, mirroring
 * the Trades header menu (useTableContextMenus.flashColumnInSidebar).
 */
interface UseMtmHeaderContextMenuParams {
  columnConfig: ColumnConfig[];
  getPinState: (columnId: string) => ColumnPinSide;
  pinColumn: (columnId: string, side: ColumnPinSide) => void;
  toggleColumnVisibility: (columnId: string) => void;
  /** Jump to the filters tab and focus the matching filter section. */
  handleFilterColumnClick: (columnId: string) => void;
  /** Columns that have a corresponding sidebar filter (controls the "Filter by" item). */
  filterableColumnIds: Set<string>;
}

export function useMtmHeaderContextMenu({
  columnConfig,
  getPinState,
  pinColumn,
  toggleColumnVisibility,
  handleFilterColumnClick,
  filterableColumnIds,
}: UseMtmHeaderContextMenuParams) {
  const setSidebarActiveTab = useUIStore((s) => s.setSidebarActiveTab);
  const setColumnsTabActive = useUIStore((s) => s.setColumnsTabActive);
  const setFilterSidebarCollapsed = useUIStore((s) => s.setFilterSidebarCollapsed);
  const setSidebarHighlightColumnId = useUIStore((s) => s.setSidebarHighlightColumnId);

  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, []);

  const flashColumnInSidebar = useCallback(
    (columnId: string) => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      setFilterSidebarCollapsed(false);
      setSidebarActiveTab('columns');
      setColumnsTabActive(true);
      setSidebarHighlightColumnId(columnId);
      highlightTimerRef.current = setTimeout(() => setSidebarHighlightColumnId(null), 2000);
    },
    [setFilterSidebarCollapsed, setSidebarActiveTab, setColumnsTabActive, setSidebarHighlightColumnId],
  );

  const [headerCtxMenu, setHeaderCtxMenu] = useState<{ columnId: string; x: number; y: number } | null>(null);
  const handleColumnContextMenu = useCallback((e: React.MouseEvent, columnId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setHeaderCtxMenu({ columnId, x: e.clientX, y: e.clientY });
  }, []);
  const closeHeaderCtxMenu = useCallback(() => setHeaderCtxMenu(null), []);

  const headerCtxMenuItems = useMemo((): ContextMenuItem[] => {
    if (!headerCtxMenu) return [];
    const colId = headerCtxMenu.columnId;
    const label = columnConfig.find((c) => c.id === colId)?.label ?? colId;
    const pin = getPinState(colId);
    const isFilterable = filterableColumnIds.has(colId);

    const items: ContextMenuItem[] = [];
    if (isFilterable) {
      items.push({
        label: `Filter by ${label}`,
        icon: <FunnelIcon className="h-3.5 w-3.5" />,
        onClick: () => handleFilterColumnClick(colId),
      });
    }
    items.push(
      {
        label: 'Pin left',
        icon: <PinLeftIcon className="h-3.5 w-3.5" />,
        dividerBefore: isFilterable,
        checked: pin === 'left',
        onClick: () => {
          pinColumn(colId, pin === 'left' ? null : 'left');
          flashColumnInSidebar(colId);
        },
      },
      {
        label: 'Pin right',
        icon: <PinRightIcon className="h-3.5 w-3.5" />,
        checked: pin === 'right',
        onClick: () => {
          pinColumn(colId, pin === 'right' ? null : 'right');
          flashColumnInSidebar(colId);
        },
      },
    );
    if (pin) {
      items.push({
        label: 'Unpin',
        icon: <XMarkIcon className="h-3.5 w-3.5" />,
        onClick: () => {
          pinColumn(colId, null);
          flashColumnInSidebar(colId);
        },
      });
    }
    items.push({
      label: 'Hide column',
      icon: <EyeSlashIcon className="h-3.5 w-3.5" />,
      dividerBefore: true,
      onClick: () => {
        toggleColumnVisibility(colId);
        flashColumnInSidebar(colId);
      },
    });
    return items;
  }, [headerCtxMenu, columnConfig, getPinState, filterableColumnIds, handleFilterColumnClick, pinColumn, toggleColumnVisibility, flashColumnInSidebar]);

  return { headerCtxMenu, headerCtxMenuItems, handleColumnContextMenu, closeHeaderCtxMenu };
}
